/**
 * Feature Catalog - Maps feature IDs to user-friendly display names and details
 * Data sourced from the feature_catalog database table
 *
 * Each feature has:
 * - name: Default English name (used as fallback)
 * - translationKey: i18n key for feature name
 * - descriptionKey: i18n key for feature description (features.{id}.description)
 * - category: Feature category for grouping
 * - description: English fallback description
 */

export const featureCatalog = {
  // Synthetic display features (not in database)
  'all_european_languages': {
    name: 'All European Languages',
    translationKey: 'features.all_european_languages',
    descriptionKey: 'features.all_european_languages_desc',
    category: 'language_pack',
    description: '22 European languages included'
  },

  // Analytics
  'basic_analytics': {
    name: 'Analytics',
    translationKey: 'features.basic_analytics',
    descriptionKey: 'features.basic_analytics_desc',
    category: 'analytics',
    description: 'Track story performance'
  },
  'advanced_analytics': {
    name: 'Advanced Analytics',
    translationKey: 'features.advanced_analytics',
    descriptionKey: 'features.advanced_analytics_desc',
    category: 'analytics',
    description: 'Detailed insights and performance tracking'
  },

  // Branding
  'white_label': {
    name: 'Custom Branding',
    translationKey: 'features.white_label',
    descriptionKey: 'features.white_label_desc',
    category: 'branding',
    description: 'Brand your content with custom styling'
  },

  // Caption/Text
  'caption_generation': {
    name: 'AI Caption Generation',
    translationKey: 'features.caption_generation',
    descriptionKey: 'features.caption_generation_desc',
    category: 'caption',
    description: 'Create captions with AI'
  },
  'caption_regeneration': {
    name: 'Regenerate Captions',
    translationKey: 'features.caption_regeneration',
    descriptionKey: 'features.caption_regeneration_desc',
    category: 'caption',
    description: 'Adjust and improve generated captions'
  },
  'custom_fonts': {
    name: 'Custom Fonts',
    translationKey: 'features.custom_fonts',
    descriptionKey: 'features.custom_fonts_desc',
    category: 'caption',
    description: 'Style captions with different fonts'
  },
  'custom_tov': {
    name: 'Custom Tone-of-Voice',
    translationKey: 'features.custom_tov',
    descriptionKey: 'features.custom_tov_desc',
    category: 'caption',
    description: 'Set your brand voice for AI captions'
  },
  'emoji_control': {
    name: 'Caption Control',
    translationKey: 'features.emoji_control',
    descriptionKey: 'features.emoji_control_desc',
    category: 'caption',
    description: 'Control emojis, hashtags & text style'
  },
  'hashtag_control': {
    name: 'Caption Control',
    translationKey: 'features.hashtag_control',
    descriptionKey: 'features.hashtag_control_desc',
    category: 'caption',
    description: 'Control emojis, hashtags & text style'
  },
  'pro_fonts': {
    name: 'Premium Fonts',
    translationKey: 'features.pro_fonts',
    descriptionKey: 'features.pro_fonts_desc',
    category: 'caption',
    description: 'Access exclusive premium fonts'
  },

  // Integration
  'instagram_insights': {
    name: 'Instagram Insights',
    translationKey: 'features.instagram_insights',
    descriptionKey: 'features.instagram_insights_desc',
    category: 'integration',
    description: 'View Instagram analytics in-app'
  },
  'webhook_notifications': {
    name: 'Webhook Notifications',
    translationKey: 'features.webhook_notifications',
    descriptionKey: 'features.webhook_notifications_desc',
    category: 'integration',
    description: 'Custom webhook integration'
  },

  // Language Support (Base languages included in plans)
  'lang_de': {
    name: 'German',
    translationKey: 'features.lang_de',
    descriptionKey: 'features.lang_de_desc',
    category: 'base_language',
    description: 'German language support'
  },
  'lang_en': {
    name: 'English',
    translationKey: 'features.lang_en',
    descriptionKey: 'features.lang_en_desc',
    category: 'base_language',
    description: 'English language support'
  },

  // Language Packs (available as add-ons)
  // European: Spanish, French, Italian, Dutch, Polish, Portuguese
  'lang_es': {
    name: 'Spanish',
    translationKey: 'features.lang_es',
    descriptionKey: 'features.lang_es_desc',
    category: 'language_addon',
    description: 'Spanish language support'
  },
  'lang_fr': {
    name: 'French',
    translationKey: 'features.lang_fr',
    descriptionKey: 'features.lang_fr_desc',
    category: 'language_addon',
    description: 'French language support'
  },
  'lang_it': {
    name: 'Italian',
    translationKey: 'features.lang_it',
    descriptionKey: 'features.lang_it_desc',
    category: 'language_addon',
    description: 'Italian language support'
  },
  'lang_nl': {
    name: 'Dutch',
    translationKey: 'features.lang_nl',
    descriptionKey: 'features.lang_nl_desc',
    category: 'language_addon',
    description: 'Dutch language support'
  },
  'lang_pl': {
    name: 'Polish',
    translationKey: 'features.lang_pl',
    descriptionKey: 'features.lang_pl_desc',
    category: 'language_addon',
    description: 'Polish language support'
  },
  'lang_pt': {
    name: 'Portuguese',
    translationKey: 'features.lang_pt',
    descriptionKey: 'features.lang_pt_desc',
    category: 'language_addon',
    description: 'Portuguese language support'
  },
  'lang_sv': {
    name: 'Swedish',
    translationKey: 'features.lang_sv',
    descriptionKey: 'features.lang_sv_desc',
    category: 'language_addon',
    description: 'Swedish language support'
  },
  'lang_no': {
    name: 'Norwegian',
    translationKey: 'features.lang_no',
    descriptionKey: 'features.lang_no_desc',
    category: 'language_addon',
    description: 'Norwegian language support'
  },
  'lang_da': {
    name: 'Danish',
    translationKey: 'features.lang_da',
    descriptionKey: 'features.lang_da_desc',
    category: 'language_addon',
    description: 'Danish language support'
  },
  'lang_fi': {
    name: 'Finnish',
    translationKey: 'features.lang_fi',
    descriptionKey: 'features.lang_fi_desc',
    category: 'language_addon',
    description: 'Finnish language support'
  },
  'lang_el': {
    name: 'Greek',
    translationKey: 'features.lang_el',
    descriptionKey: 'features.lang_el_desc',
    category: 'language_addon',
    description: 'Greek language support'
  },
  'lang_cs': {
    name: 'Czech',
    translationKey: 'features.lang_cs',
    descriptionKey: 'features.lang_cs_desc',
    category: 'language_addon',
    description: 'Czech language support'
  },
  'lang_hu': {
    name: 'Hungarian',
    translationKey: 'features.lang_hu',
    descriptionKey: 'features.lang_hu_desc',
    category: 'language_addon',
    description: 'Hungarian language support'
  },
  'lang_ro': {
    name: 'Romanian',
    translationKey: 'features.lang_ro',
    descriptionKey: 'features.lang_ro_desc',
    category: 'language_addon',
    description: 'Romanian language support'
  },
  'lang_bg': {
    name: 'Bulgarian',
    translationKey: 'features.lang_bg',
    descriptionKey: 'features.lang_bg_desc',
    category: 'language_addon',
    description: 'Bulgarian language support'
  },
  'lang_hr': {
    name: 'Croatian',
    translationKey: 'features.lang_hr',
    descriptionKey: 'features.lang_hr_desc',
    category: 'language_addon',
    description: 'Croatian language support'
  },
  'lang_sk': {
    name: 'Slovak',
    translationKey: 'features.lang_sk',
    descriptionKey: 'features.lang_sk_desc',
    category: 'language_addon',
    description: 'Slovak language support'
  },
  'lang_sl': {
    name: 'Slovenian',
    translationKey: 'features.lang_sl',
    descriptionKey: 'features.lang_sl_desc',
    category: 'language_addon',
    description: 'Slovenian language support'
  },
  'lang_lt': {
    name: 'Lithuanian',
    translationKey: 'features.lang_lt',
    descriptionKey: 'features.lang_lt_desc',
    category: 'language_addon',
    description: 'Lithuanian language support'
  },
  'lang_lv': {
    name: 'Latvian',
    translationKey: 'features.lang_lv',
    descriptionKey: 'features.lang_lv_desc',
    category: 'language_addon',
    description: 'Latvian language support'
  },
  'lang_et': {
    name: 'Estonian',
    translationKey: 'features.lang_et',
    descriptionKey: 'features.lang_et_desc',
    category: 'language_addon',
    description: 'Estonian language support'
  },
  'lang_tr': {
    name: 'Turkish',
    translationKey: 'features.lang_tr',
    descriptionKey: 'features.lang_tr_desc',
    category: 'language_addon',
    description: 'Turkish language support'
  },

  // Asian Languages (available as add-on)
  'lang_ja': {
    name: 'Japanese',
    translationKey: 'features.lang_ja',
    descriptionKey: 'features.lang_ja_desc',
    category: 'language_addon',
    description: 'Japanese language support'
  },
  'lang_ko': {
    name: 'Korean',
    translationKey: 'features.lang_ko',
    descriptionKey: 'features.lang_ko_desc',
    category: 'language_addon',
    description: 'Korean language support'
  },
  'lang_th': {
    name: 'Thai',
    translationKey: 'features.lang_th',
    descriptionKey: 'features.lang_th_desc',
    category: 'language_addon',
    description: 'Thai language support'
  },
  'lang_vi': {
    name: 'Vietnamese',
    translationKey: 'features.lang_vi',
    descriptionKey: 'features.lang_vi_desc',
    category: 'language_addon',
    description: 'Vietnamese language support'
  },
  'lang_zh_cn': {
    name: 'Chinese (Simplified)',
    translationKey: 'features.lang_zh_cn',
    descriptionKey: 'features.lang_zh_cn_desc',
    category: 'language_addon',
    description: 'Simplified Chinese support'
  },
  'lang_zh_tw': {
    name: 'Chinese (Traditional)',
    translationKey: 'features.lang_zh_tw',
    descriptionKey: 'features.lang_zh_tw_desc',
    category: 'language_addon',
    description: 'Traditional Chinese support'
  },

  // Ecommerce
  'ecommerce_product_videos': {
    name: 'Product Videos',
    translationKey: 'features.ecommerce_product_videos',
    descriptionKey: 'features.ecommerce_product_videos_desc',
    category: 'ecommerce',
    description: 'AI-generated product videos from your catalog'
  },
  'ecommerce_product_feeds': {
    name: 'Product Feed Import',
    translationKey: 'features.ecommerce_product_feeds',
    descriptionKey: 'features.ecommerce_product_feeds_desc',
    category: 'ecommerce',
    description: 'Import products from your shop feed'
  },
  'ecommerce_link_in_bio': {
    name: 'Link-in-Bio',
    translationKey: 'features.ecommerce_link_in_bio',
    descriptionKey: 'features.ecommerce_link_in_bio_desc',
    category: 'ecommerce',
    description: 'Shoppable link-in-bio page with product links'
  },
  'ecommerce_click_analytics': {
    name: 'Click Analytics',
    translationKey: 'features.ecommerce_click_analytics',
    descriptionKey: 'features.ecommerce_click_analytics_desc',
    category: 'ecommerce',
    description: 'Track clicks and conversions on product stories'
  },

  // Media
  'bulk_upload': {
    name: 'Bulk Upload',
    translationKey: 'features.bulk_upload',
    descriptionKey: 'features.bulk_upload_desc',
    category: 'media',
    description: 'Upload multiple files at once'
  },
  'gif_rendering': {
    name: 'GIFs in Stories',
    translationKey: 'features.gif_rendering',
    descriptionKey: 'features.gif_rendering_desc',
    category: 'media',
    description: 'Create and share animated GIFs'
  },
  'image_upload': {
    name: 'Image Upload',
    translationKey: 'features.image_upload',
    descriptionKey: 'features.image_upload_desc',
    category: 'media',
    description: 'Upload images to stories'
  },
  'video_upload': {
    name: 'Video Upload',
    translationKey: 'features.video_upload',
    descriptionKey: 'features.video_upload_desc',
    category: 'media',
    description: 'Upload videos to stories'
  },

  // Scheduling
  'post_scheduling': {
    name: 'Story Scheduling',
    translationKey: 'features.post_scheduling',
    descriptionKey: 'features.post_scheduling_desc',
    category: 'scheduling',
    description: 'Schedule stories in advance'
  },
  'posting_interval_limit': {
    name: 'Frequent Posting',
    translationKey: 'features.posting_interval_limit',
    descriptionKey: 'features.posting_interval_limit_desc',
    category: 'scheduling',
    description: 'Post as frequently as every hour'
  },
  'schedule_coverage': {
    name: 'Schedule Planning',
    translationKey: 'features.schedule_coverage',
    descriptionKey: 'features.schedule_coverage_desc',
    category: 'scheduling',
    description: 'Plan your posting strategy'
  },

}

/**
 * Get feature details by ID
 */
export const getFeature = (featureId) => {
  return featureCatalog[featureId] || {
    name: featureId,
    category: 'unknown',
    description: ''
  }
}

/**
 * Get grouped features for comparison display
 * Groups features by category for better UX
 */
export const getFeaturesByCategory = (featureIds) => {
  const grouped = {}

  featureIds.forEach(featureId => {
    const feature = getFeature(featureId)
    const category = feature.category

    if (!grouped[category]) {
      grouped[category] = []
    }
    grouped[category].push({
      id: featureId,
      ...feature
    })
  })

  return grouped
}

/**
 * Get important features for comparison table
 * Filters to only key features that are meaningful for plan comparison
 *
 * Excludes:
 * - base_language (de, en) - shown in all plans
 * - language_addon (individual languages) - shown via language packs
 * - analytics (basic/advanced) - technical feature
 */
export const getComparisonFeatures = (allFeatureIds) => {
  // These categories are important for plan comparison
  const importantCategories = ['media', 'caption', 'scheduling', 'integration', 'branding', 'ecommerce']

  // Hide individual language features - they're shown through language packs
  const excludeCategories = ['base_language', 'language_addon', 'analytics']

  const importantFeatures = []

  allFeatureIds.forEach(featureId => {
    const feature = getFeature(featureId)
    if (importantCategories.includes(feature.category)) {
      importantFeatures.push({
        id: featureId,
        ...feature
      })
    }
  })

  return importantFeatures
}

/**
 * Get language features from feature IDs
 * Returns just the language features
 */
export const getLanguageFeatures = (featureIds) => {
  return featureIds
    .filter(id => getFeature(id).category === 'language')
    .map(id => getFeature(id))
}

/**
 * Get media features from feature IDs
 * Returns just the media features
 */
export const getMediaFeatures = (featureIds) => {
  return featureIds
    .filter(id => getFeature(id).category === 'media')
    .map(id => getFeature(id))
}

/**
 * Deduplicate features by name
 * Useful when features like emoji_control and hashtag_control share the same name
 * Only shows each unique feature name once
 */
export const deduplicateFeatures = (featureIds) => {
  const seen = new Set()
  const result = []

  featureIds.forEach(featureId => {
    const feature = getFeature(featureId)
    // Use name as dedup key - if same name already shown, skip
    if (!seen.has(feature.name)) {
      seen.add(feature.name)
      result.push(featureId)
    }
  })

  return result
}

/**
 * Get translated feature description with i18n
 * Uses translation function to get translated description
 * Falls back to English description if translation not found
 *
 * Usage: getTranslatedDescription(featureId, t)
 * where t is the i18n translation function from useTranslation()
 */
export const getTranslatedDescription = (featureId, t) => {
  const feature = getFeature(featureId)
  if (!t) return feature.description

  // Use the descriptionKey if available
  const key = feature.descriptionKey || `features.${featureId}_desc`
  const translated = t(key, { defaultValue: null })

  // If translation exists, use it; otherwise fall back to default English description
  return translated && translated !== key ? translated : feature.description
}

/**
 * Get translated feature name with i18n
 * Uses translation function to get translated name
 * Falls back to English name if translation not found
 */
export const getTranslatedName = (featureId, t) => {
  const feature = getFeature(featureId)
  if (!t) return feature.name

  // Use the existing translationKey
  const translated = t(feature.translationKey, { defaultValue: null })
  return translated && translated !== feature.translationKey ? translated : feature.name
}
